/**
 * @file components/audit-tasks/PersonDaysCard.tsx
 * @description 稽核人天數卡片（2026-09-15 回報序28）。
 *
 * 【稽核總人天數】【其中現場稽核】【稽核準備及報告】原於「回覆派案」接收案件時填寫，
 * 客戶要求移至「稽核計畫」階段，與稽核排程計劃放在一起，並可隨時修改。
 *
 * 權限：任務稽核員本人或管理組（後端 PATCH /api/audit-tasks/:id/person-days 雙重校驗）。
 */

import { useState } from 'react';
import { Button, Card, Descriptions, Form, InputNumber, Modal, Space, Typography } from 'antd';
import { Clock } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userCan, type AuditTaskDetail } from '@zhcb/shared';
import { apiClient, ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { message } from '@/lib/antd-app';

interface Props {
  task: AuditTaskDetail;
}

/** 表單值（InputNumber 數值，提交時轉字串） */
interface PersonDaysFormValues {
  totalPersonDays: number | null;
  fieldDays: number | null;
  prepReportDays: number | null;
}

/** 字串人天數 → 數值（空值回 null） */
function toNum(v: string | null): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** 數值 → 字串人天數（保留一位小數內原值；空值回 null） */
function toStr(v: number | null | undefined): string | null {
  return v == null ? null : String(v);
}

/** 稽核人天數卡片 */
export function PersonDaysCard({ task }: Props) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<PersonDaysFormValues>();
  /** 可編輯：任務稽核員本人或管理組 */
  const canEdit = !!user && (user.id === task.assigneeId || userCan(user, 'case.dispatch'));

  const saveMutation = useMutation({
    mutationFn: (v: PersonDaysFormValues) =>
      apiClient.patch<{ ok: boolean }>(`/api/audit-tasks/${task.id}/person-days`, {
        totalPersonDays: toStr(v.totalPersonDays),
        fieldDays: toStr(v.fieldDays),
        prepReportDays: toStr(v.prepReportDays),
      }),
    onSuccess: () => {
      message.success('稽核人天數已更新');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
    },
    onError: (err: unknown) => {
      const e = err as ApiError;
      console.error('[PersonDaysCard] update person-days failed', err);
      message.error(e.message || '更新失敗，請稍後重試');
    },
  });

  /** 顯示值：未填顯示「—」 */
  const show = (v: string | null) =>
    v ? `${v} 天` : <Typography.Text type="secondary">—</Typography.Text>;

  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <Clock size={14} />
          稽核人天數
        </Space>
      }
      extra={
        canEdit && (
          <Button
            size="small"
            type="primary"
            onClick={() => {
              form.setFieldsValue({
                totalPersonDays: toNum(task.totalPersonDays),
                fieldDays: toNum(task.fieldDays),
                prepReportDays: toNum(task.prepReportDays),
              });
              setOpen(true);
            }}
          >
            {task.totalPersonDays ? '修改' : '填寫'}
          </Button>
        )
      }
    >
      <Descriptions size="small" column={3}>
        <Descriptions.Item label="稽核總人天數">{show(task.totalPersonDays)}</Descriptions.Item>
        <Descriptions.Item label="其中現場稽核">{show(task.fieldDays)}</Descriptions.Item>
        <Descriptions.Item label="稽核準備及報告">{show(task.prepReportDays)}</Descriptions.Item>
      </Descriptions>

      <Modal
        title="稽核人天數"
        open={open}
        onOk={() => void form.validateFields().then((v) => saveMutation.mutate(v))}
        confirmLoading={saveMutation.isPending}
        onCancel={() => setOpen(false)}
        okText="儲存"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space size={12} style={{ display: 'flex' }} align="start" wrap>
            {(
              [
                ['totalPersonDays', '稽核總人天數', '請填寫總人天數'],
                ['fieldDays', '其中現場稽核', '請填寫現場稽核天數'],
                ['prepReportDays', '稽核準備及報告', '請填寫準備及報告天數'],
              ] as const
            ).map(([name, label, msg]) => (
              <Form.Item
                key={name}
                name={name}
                label={label}
                rules={[{ required: true, message: msg }]}
              >
                <InputNumber
                  min={0}
                  max={999}
                  step={0.5}
                  precision={1}
                  addonAfter="天"
                  style={{ width: 150 }}
                />
              </Form.Item>
            ))}
          </Space>
        </Form>
      </Modal>
    </Card>
  );
}
